/**
 * AR invoices advanced-filter document — a versioned value the web filter
 * panel (`ARInvoiceFilterPanel`) edits and evaluates CLIENT-SIDE over the
 * complete AR invoice index. One schema + ONE evaluator, both in
 * @releve/shared (the places/Impact template: `placesFilters.ts`,
 * `sustainabilityFilters.ts`), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyARInvoiceFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build; same
 * posture here).
 *
 * ── THIS IS A MOVE, NOT AN EXPANSION ────────────────────────────────────────
 * v1's vocabulary is EXACTLY the nine columns the grid used to mark
 * `filterable: true` (arInvoiceGridColumns.tsx), so the conversion to the
 * panel is behaviour-preserving and reviewable. Deliberately absent: the money
 * columns. Amount ranges would be net-new UI, and the AR money cells carry a
 * PER-ROW currency (`ar_account_currency`) — a single unlabelled numeric range
 * across a mixed-currency index would compare 100 USD with 100 EUR. Adding
 * them is a design question, not a port.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The AR grid ends with ZERO filterable columns.
 *
 * v1 fields are `invoice.<field>` over the MAPPED grid rows
 * (`ARInvoiceGridRow`), not the wire row — `status` is null-normalized and the
 * date columns are reduced to local calendar days by the mapper, so evaluation
 * runs AFTER the mapper and BEFORE the grid shell (`hasUpstreamFilters`). The
 * field string carries its namespace, so a later vocabulary (line-level
 * fields, say) stays additive.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the nine ex-`filterable` columns
// ---------------------------------------------------------------------------
export const AR_INVOICE_FILTER_STRING_FIELDS = [
    "invoice.status",
    "invoice.customer_name",
    "invoice.tax_summary",
    "invoice.project_id",
    "invoice.ar_account_id",
    "invoice.fund_id",
];
export const AR_INVOICE_FILTER_DATE_FIELDS = [
    "invoice.invoice_date",
    "invoice.due_date",
    "invoice.record_created_at",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(AR_INVOICE_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(AR_INVOICE_FILTER_DATE_FIELDS);
export const arInvoiceFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const arInvoiceFilterDocumentSchema = makeFilterDocumentSchema(arInvoiceFilterConditionSchema);
/** The empty document — "no filters active". */
export const EMPTY_AR_INVOICE_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary (the reason `sustainabilityFilters` keeps them: a new vocabulary
 * entry is a `TS2366: Function lacks ending return statement` until it is
 * handled here, instead of compiling clean and filtering the wrong column).
 */
function stringValue(row, field) {
    switch (field) {
        case "invoice.status":
            return row.status;
        case "invoice.customer_name":
            return row.customer_name ?? "";
        case "invoice.tax_summary":
            // Already `''` (never null) on an invoice with no lines.
            return row.tax_summary;
        case "invoice.project_id":
            return row.project_id ?? "";
        case "invoice.ar_account_id":
            return row.ar_account_id ?? "";
        case "invoice.fund_id":
            return row.fund_id ?? "";
    }
}
/**
 * The LOCAL calendar day (`yyyy-MM-dd`) to compare — verbatim for the two
 * mapper-normalized columns, reduced from the instant for the raw
 * `record_created_at` timestamp. Compared as STRINGS (lexicographic order is
 * date order for this shape); no `new Date()` round-trip on a date-only value.
 */
function dateValue(row, field) {
    switch (field) {
        case "invoice.invoice_date":
            return row.invoice_date;
        case "invoice.due_date":
            return row.due_date;
        case "invoice.record_created_at":
            return toLocalCalendarDay(row.record_created_at);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped AR invoice rows (AND of all conditions —
 * v1 `match` is 'all' only). An empty document returns the input array
 * unchanged (identity — callers can rely on reference equality to skip
 * re-renders).
 */
export function applyARInvoiceFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveARInvoiceConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=arInvoiceFilters.js.map